import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import qs from 'qs'
import { OffsetLimit, OffsetLimitPaginationResponse } from '~/api/common'
import { User } from '~/api/users'
import fetch from '~/common/fetch'
import { FetchOptions } from '~/common/fetchHelpers'
import { queryOptions } from '~/common/react-query/queryOptions'
import { queryClient } from '~/common/react-query/queryClient'

export enum AuthProviderId {
  SAML = 2,
  VNDLY_GOOGLE_OAUTH2 = 3,
  NOLOGIN = 4,
  VNDLY = 1
}

export enum UserRole {
  VENDOR = 'vendor',
  CANDIDATE = 'candidate',
  EMPLOYER = 'employer'
}

export type LastLoginResponse = {
  username?: string
  name?: string
}

export type BaseAccountMeResponse = {
  id: number
  full_name: string
  email: string
  rbac_role: string
  tenant: string
  environment: string
  timezone: string
  locale: string
  default_conf_num?: string
  default_conf_num_country_code?: string
  preferences?: any
}

export interface VendorAccountMeResponse extends BaseAccountMeResponse {
  role: UserRole.VENDOR
  employer_id: null
  vendor_id: number
  vendor_entity_id: number
}

export interface CandidateAccountMeResponse extends BaseAccountMeResponse {
  role: UserRole.CANDIDATE
  employer_id: null
  vendor_entity_id: null
}

export interface EmployerAccountMeResponse extends BaseAccountMeResponse {
  role: UserRole.EMPLOYER
  employer_id: number
  vendor_entity_id: null
}

export type AccountMeResponse = (VendorAccountMeResponse | CandidateAccountMeResponse | EmployerAccountMeResponse) & {
  role: UserRole
  isVendorRole: boolean
  isCandidateRole: boolean
  isEmployerRole: boolean
}

export type IsMFAVerifiedResponse = Record<string, never>
export type ResetMFAResponse = Record<string, never>
export type ResetUserMFAResponse = Record<string, never>

export interface CanResetUserMFAResponse {
  can_reset_user_mfa: boolean
}

export type ProfileResponse = (VendorProfileResponse & CandidateProfileResponse & EmployerProfileResponse) & {
  profile: UserProfile
}

interface UserProfile {
  first_name: string
  last_name: string
  middle_name: string | null
  preferred_first_name: string | null
  preferred_last_name: string | null
  email: string | null
  locale: string | null
  timezone: string | null
  specified_timezone_or_default: string
  specified_locale_or_default: string
  locale_display_name: string
  is_impersonated: boolean
  auth_provider: number
  role: string
  is_mfa_required: boolean
}

interface VendorProfileResponse {
  contact_number: string | null
  contact_country_code: string | null
  formatted_contact_number: string | null
  country_code: string | null
  company_name: string | null
  company_id: number | null
}

interface CandidateProfileResponse {
  contact_number: string | null
  contact_country_code: string | null
  formatted_contact_number: string | null
  country_code: string | null
  company_name: string | null
}

interface EmployerProfileResponse {
  contact_number: string | null
  contact_country_code: string | null
  formatted_contact_number: string | null
  country_code: string | null
  company_name: string | null
  is_complete: boolean
  default_conference_number: string | null
  default_interview_location: string | null
  default_web_conference_details: string | null
}

export type ProfileFormData = EmployerProfileFormData | VendorProfileFormData | ContractorProfileFormData

interface UserProfileFormData {
  first_name: string
  last_name: string
  middle_name: string | null
  preferred_first_name: string | null
  preferred_last_name: string | null
  locale: string | null
  timezone: string | null
}

interface EmployerProfileFormData {
  profile: UserProfileFormData
  contact_number: string | null
  contact_country_code: string | null
  default_conference_number: string | null
  default_interview_location: string | null
  default_web_conference_details: string | null
}

interface VendorProfileFormData extends UserProfileFormData {
  profile: UserProfileFormData
  contact_number: string | null
  contact_country_code: string | null
}
interface ContractorProfileFormData extends UserProfileFormData {
  profile: UserProfileFormData
  contact_number: string | null
  contact_country_code: string | null
}

export type DelegatedUser = Pick<User, 'id' | 'first_name' | 'last_name' | 'full_name' | 'email'> & { company?: string }
export type DelegatedUserListResponse = OffsetLimitPaginationResponse<DelegatedUser>
export type DelegatedUserListFilters = Partial<OffsetLimit> & {
  query?: string
  ordering?: 'last_name' | 'company' | '-last_name' | '-company'
}

export type DelegationSettingsConfigResponse = {
  can_read_delegation_settings: boolean
  can_edit_delegation_settings: boolean
  allow_indefinite_delegation: boolean
}

export interface CreateDelegatePayload {
  delegate_id: number | undefined
  target_id: number | undefined
  starts_date: string
  expires_date?: string
}

const THIRTY_MINUTES = 1000 * 60 * 30
const responseCache = { ttl: THIRTY_MINUTES }
export const accountsApi = {
  me: async (queryParams?: Record<string, any>, fetchOpts?: FetchOptions): Promise<AccountMeResponse> => {
    const q = queryParams ? `?${qs.stringify(queryParams)}` : ''
    const response: AccountMeResponse = await fetch.get(`/api/v2/accounts/me/${q}`, { responseCache, ...fetchOpts })
    return Object.assign(response ?? {}, {
      isVendorRole: response?.role === UserRole.VENDOR,
      isCandidateRole: response?.role === UserRole.CANDIDATE,
      isEmployerRole: response?.role === UserRole.EMPLOYER
    })
  },
  isAuthenticated(): Promise<{ is_authenticated: boolean }> {
    const FIVE_SECONDS = 1000 * 5
    const responseCache = { ttl: FIVE_SECONDS }
    return fetch.get('/api/v2/accounts/is_authenticated', { responseCache })
  },
  isMFAVerified: (): Promise<IsMFAVerifiedResponse> => fetch.get(`/api/v2/accounts/is_mfa_verified/`),
  resetMFA: (): Promise<ResetMFAResponse> => fetch.post(`/accounts/reset_mfa/`),
  resetUserMFA: (user_id: number): Promise<ResetUserMFAResponse> =>
    fetch.post(`/api/v2/accounts/reset_user_mfa/`, { user_id }),
  canResetUserMFA: (user_id: number): Promise<CanResetUserMFAResponse> =>
    fetch.post(`/api/v2/accounts/can_reset_user_mfa/`, { user_id }),
  delegateRequestUserList: (filters?: DelegatedUserListFilters): Promise<DelegatedUserListResponse> =>
    fetch.get(`/api/v2/accounts/delegates/users/request/${qs.stringify(filters, { addQueryPrefix: true })}`),
  delegateGrantUserList: (filters?: DelegatedUserListFilters): Promise<DelegatedUserListResponse> =>
    fetch.get(`/api/v2/accounts/delegates/users/to/${qs.stringify(filters, { addQueryPrefix: true })}`),
  createDelegate: (data: CreateDelegatePayload): Promise<void> => fetch.post(`/accounts/delegates/create/`, data),
  delegationSettingsConfig: (): Promise<DelegationSettingsConfigResponse> =>
    fetch.get(`/api/v2/accounts/delegation_settings/config`),
  profile: (): Promise<ProfileResponse> => fetch.get(`/api/v2/accounts/profile/`),
  updateProfile: (userProfileData: ProfileFormData): Promise<ProfileResponse> =>
    fetch.put(`/api/v2/accounts/profile/`, userProfileData)
}

export const accountsKeys = {
  all: ['accounts'] as const,
  me: (filters?: Record<string, any>) => [...accountsKeys.all, 'me', filters] as const,
  is_authenticated: () => [...accountsKeys.all, 'is_mfa_verified'] as const,
  is_mfa_verified: () => [...accountsKeys.all, 'is_mfa_verified'] as const,
  delegate_request_user_list: (filters?: DelegatedUserListFilters) =>
    [...accountsKeys.all, 'delegate_request_user_list', filters] as const,
  delegate_grant_user_list: (filters?: DelegatedUserListFilters) =>
    [...accountsKeys.all, 'delegate_grant_user_list', filters] as const,
  delegation_settings_permissions: ['delegation_settings_permissions'] as const,
  canResetUserMFA: (user_id: number) => [...accountsKeys.all, 'can_reset_user_mfa', user_id] as const,
  profile: () => [...accountsKeys.all, 'profile'] as const
}

export type AccountMeParams = {
  full_details?: boolean
}
const accountsMe = (queryParams?: AccountMeParams, fetchOptions?: FetchOptions) =>
  queryOptions({
    queryKey: accountsKeys.me(queryParams),
    queryFn: () => accountsApi.me(queryParams, fetchOptions),
    staleTime: Infinity
  })

export const useAccountMeQuery = (queryParams?: AccountMeParams, fetchOptions?: FetchOptions) => {
  const result = useQuery(accountsMe(queryParams, fetchOptions))

  return Object.assign(result, {
    role: result.data?.role,
    isVendorRole: result.data?.role === UserRole.VENDOR,
    isCandidateRole: result.data?.role === UserRole.CANDIDATE,
    isEmployerRole: result.data?.role === UserRole.EMPLOYER
  })
}

export const useLastLoginQuery = () => {
  return useQuery<LastLoginResponse>({
    queryKey: [...accountsKeys.all, 'accounts_last_login'],
    queryFn: () => fetch.get('/api/v2/accounts/last_login')
  })
}

export const useMyProfileQuery = () => {
  return useQuery({ queryKey: accountsKeys.profile(), queryFn: () => accountsApi.profile() })
}

export const useUpdateProfileMutation = () => {
  return useMutation({
    mutationFn: (profileData: ProfileFormData) => accountsApi.updateProfile(profileData),
    onSuccess: async () => {
      await queryClient.invalidateQueries(accountsKeys.profile())
      await queryClient.invalidateQueries(['navigationMenu', 'config'])
    }
  })
}

export const useIsAuthenticatedQuery = (options?: UseQueryOptions<{ is_authenticated: boolean }>) => {
  return useQuery({
    queryKey: accountsKeys.is_authenticated(),
    queryFn: () => accountsApi.isAuthenticated(),
    ...options
  })
}

export const useIsMFAVerifiedQuery = (options: UseQueryOptions<IsMFAVerifiedResponse>) => {
  return useQuery({ queryKey: accountsKeys.is_mfa_verified(), queryFn: accountsApi.isMFAVerified, ...options })
}

export const useDelegationRequestUserListQuery = (filters?: DelegatedUserListFilters) => {
  const finalFilters = { limit: 10, ...filters }
  const key = accountsKeys.delegate_request_user_list(finalFilters)
  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam = 0 }) => {
      return accountsApi.delegateRequestUserList({ offset: pageParam, ...filters })
    },
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })
}

export const useCreateDelegateMutation = (opts?: UseMutationOptions<void, any, CreateDelegatePayload>) => {
  return useMutation({
    mutationFn: createDelegatePayload => accountsApi.createDelegate(createDelegatePayload),
    ...opts
  })
}

export const useDelegationGrantUserListQuery = (filters?: DelegatedUserListFilters) => {
  const finalFilters = { limit: 10, ...filters }
  const key = accountsKeys.delegate_grant_user_list(finalFilters)
  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam = 0 }) => {
      return accountsApi.delegateGrantUserList({ offset: pageParam, ...filters })
    },
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })
}

export const useDelegationSettingsConfigQuery = () => {
  return useQuery({
    queryKey: accountsKeys.delegation_settings_permissions,
    queryFn: () => {
      return accountsApi.delegationSettingsConfig()
    }
  })
}

export const useCanUserResetMFAQuery = (user_id: number, options?: { enabled: boolean }) => {
  return useQuery({
    queryKey: accountsKeys.canResetUserMFA(user_id),
    queryFn: () => {
      return accountsApi.canResetUserMFA(user_id)
    },
    enabled: options?.enabled
  })
}

// Getting tinyMCE locale using vndly locale
// Until refactor will need to be updated as more language packs are available
// TODO Future refactor to have this auto mapped between VNDLY and tinyMCE locales
const tinyMCELocale = new Map<string, string>([
  ['', 'en'],
  ['da_DK', 'da'],
  ['de_DE', 'de'],
  ['en_US', 'en'],
  ['en', 'en'],
  ['es', 'es'],
  ['fi_FI', 'fi'],
  ['fr_FR', 'fr_FR'],
  ['fr_CA', 'fr_FR'],
  ['it_IT', 'it'],
  ['ja_JP', 'ja'],
  ['ko_KR', 'ko_KR'],
  ['nb_NO', 'nb_NO'],
  ['nl_NL', 'nl'],
  ['pl_PL', 'pl'],
  ['pt_BR', 'pt_BR'],
  ['sv_SE', 'sv_SE'],
  ['zh_Hans_CN', 'zh_CN'],
  ['zh_Hans_TW', 'zh_TW']
])

export const getTinyMceLocale = (CurrentLocale = 'en') => {
  // checks if value exists exactly and defaults to en if not
  // if no exact match checks if there is anything comparable to CurrentLocale
  // returns any exact or partial match if found otherwise returns the default of en

  let locale
  if (tinyMCELocale.has(CurrentLocale)) {
    locale = tinyMCELocale.get(CurrentLocale)
  } else if (CurrentLocale && Array.from(tinyMCELocale.keys()).find(k => k.includes(CurrentLocale.split('_')[0]))) {
    locale = tinyMCELocale.get(`${Array.from(tinyMCELocale.keys()).find(k => k.includes(CurrentLocale.split('_')[0]))}`)
  } else {
    // This is the default language if nothing matches what is passed in
    locale = 'en'
  }
  return locale
}
